import { type JSX, MouseEvent, useContext } from "react";
import { CoreRow } from "@tanstack/react-table";
import { RESOURCE_NAME } from "common/authorization";
import { buildLink } from "common/routing";
import { formatDocumentUploadName } from "common-client/utils/documentUploads";
import { colors } from "../../../stitches.config";
import { AuthContext } from "../../Authorization/AuthContext";
import { useEditTaskModal } from "../../Tasks/Form/EditTaskForm";
import { IconNames } from "../Icons/LucideIcons";
import { ExpandableCell } from "./ExpandableCell";

export type TaskForCell = {
  name: string;
  id: string;
};

export const filesCell = ({
  row,
  isSubmission,
}: {
  row: CoreRow<any>["original"];
  isSubmission?: boolean;
}): Maybe<JSX.Element> => {
  const { authorized } = useContext(AuthContext);
  const canViewDocumentDetail = authorized({
    resource: RESOURCE_NAME.DOCUMENT_UPLOAD,
    permission: "view_details",
  });

  const tags = row?.map(
    (f: { issuedAt: string; createdAt: string; type: string; id: string }) => {
      let href;

      if (canViewDocumentDetail) {
        href = buildLink(
          "documentUploadDetail",
          {
            id: f.id,
          },
          {},
        );
      } else {
        href = buildLink("documentUploadFile", { id: f.id }, {});
      }

      return {
        label: formatDocumentUploadName({
          isSubmission,
          createdAt: f.createdAt,
          issuedAt: f.issuedAt,
          accountDocumentTypeName: f.type,
        }),
        iconName: "file-text",
        href,
      };
    },
  );

  return <ExpandableCell tags={tags} />;
};

export const tagsCell = ({
  row,
  iconName,
  color,
  iconColor,
  clickable = true,
}: {
  row: CoreRow<any>["original"];
  iconName?: IconNames;
  color?: keyof typeof colors;
  iconColor?: keyof typeof colors;
  clickable?: boolean;
}): Maybe<JSX.Element> => {
  const tags = row?.map((tag: string) => ({
    label: tag,
    iconName,
    color,
    iconColor,
  }));

  return <ExpandableCell tags={tags} clickable={clickable} />;
};

export const TaskTag = ({ task }: { task: TaskForCell }) => {
  const [showTaskModal, hideTaskModal] = useEditTaskModal({
    taskId: task.id,
    onSuccess: () => {
      hideTaskModal();
    },
  });

  return {
    label: task.name,
    onClick: (e: MouseEvent) => {
      e.stopPropagation();
      showTaskModal();
    },
  };
};

export const TasksCell = ({ tasks }: { tasks: Array<TaskForCell> }) => {
  if (tasks.length === 0) {
    return null;
  }

  const tags = tasks.map((task: { name: string; id: string }) =>
    TaskTag({ task }),
  );

  return <ExpandableCell tags={tags} />;
};
